import {
  ADD_TO_CART,
  APPLY_PROMO_CODE,
  CLEAR_CART,
  IDENTIFY_USER_EXIST_ON_DEVICE_SUCCESSFUL,
  LOGIN_SUCCESS,
  LOGOUT_SUCCESS,
  REMOVE_FROM_CART, REMOVE_PROMO_CODE,
  SET_CART_ITEMS_FROM_STORAGE, SET_USER, UPDATE_QUANTITY} from '../actionConstants';
import {getCartItemsByUserId, saveCartToStorage} from '../localStorage/localCartStorage';

import {createReducer} from './reducerUtils';

const initialState = {
  userId: null,
  items: [],
  totalQuantity: 0,
  totalPrice: 0,
  promoCode: null,
  discountValue: 0,
};

// Helper to calculate totalQuantity and totalPrice
const calculateTotals = items => {
  const totalQuantity = items.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );

  const totalPrice = items.reduce(
    (sum, item) => sum + item.totalAmount,
    0,
  );

  return {totalQuantity, totalPrice};
};
const setCartFromStorage = (state, items) => {
  const totalQuantity = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce((s, i) => s + i.totalAmount, 0);

  return {
    ...state,
    items,
    totalQuantity,
    totalPrice,
  };
};

// Add item to cart
const addToCart = (state, payload, initialState) => {
  const existingItem = state.items.find(
    item =>
      item.productId === payload.productId &&
      item.size === payload.size &&
      item.color === payload.color,
  );

  let updatedItems;

  if (existingItem) {
    updatedItems = state.items.map(item =>
      item.productId === payload.productId &&
      item.size === payload.size &&
      item.color === payload.color
        ? {
          ...item,
          quantity: item.quantity + payload.quantity,
          totalAmount:
              item.pricePerUnit *
              (item.quantity + payload.quantity),
        }
        : item,
    );
  } else {
    updatedItems = [
      ...state.items,
      {
        ...payload,
        totalAmount: payload.pricePerUnit * payload.quantity,
      },
    ];
  }

  const {totalQuantity, totalPrice} = calculateTotals(updatedItems);
  saveCartToStorage(updatedItems, state.userId);

  return {
    ...state,
    items: updatedItems,
    totalQuantity,
    totalPrice,
  };
};

// Remove item from cart
const removeFromCart = (state, {productId, initialState, size}) => {
  const updatedItems = state.items.filter(
    item => !(item.productId === productId && item.size === size),
  );
  const {totalQuantity, totalPrice} = calculateTotals(updatedItems);
  saveCartToStorage(updatedItems, state.userId);

  return {...state, items: updatedItems, totalQuantity, totalPrice};
};

const onAuthRehydrate = (state, payload) => {
  const userId = payload?.id;
  if (!userId) {return state;}

  const items = getCartItemsByUserId(userId);

  const totalQuantity = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce((s, i) => s + i.totalAmount, 0);

  return {
    ...state,
    userId,
    items,
    totalQuantity,
    totalPrice,
  };
};

// Update quantity of a cart item
const updateQuantity = (state, {productId, size, quantity}) => {
  const updatedItems = state.items.map(item => {
    if (item.productId === productId && item.size === size) {
      const totalAmount =
        item.pricePerUnit * quantity +
        (item.extraPrice || 0) -
        (item.discount || 0) -
        (item.promoDiscount || 0);
      return {...item, quantity, totalAmount};
    }
    return item;
  });

  const {totalQuantity, totalPrice} = calculateTotals(updatedItems);
  saveCartToStorage(updatedItems, state.userId);

  return {...state, items: updatedItems, totalQuantity, totalPrice};
};

// Apply promo code
const applyPromoCode = (state, {promoCode, promocodeInfo}) => {
  const isApplied = !!promocodeInfo?.code;

  const updatedItems = state.items.map(item => {
    const promoDiscount = promocodeInfo?.discountValue || 0;
    const totalAmount =
      item.pricePerUnit * item.quantity +
      (item.extraPrice || 0) -
      (item.discount || 0) -
      promoDiscount;

    return {
      ...item,
      promoCode: isApplied ? promocodeInfo.code : null,
      promocodeInfo: isApplied ? promocodeInfo : null,
      promoDiscount: isApplied ? promoDiscount : 0,
      totalAmount,
    };

  });

  const totalQuantity = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = updatedItems.reduce((sum, item) => sum + item.totalAmount, 0);
  saveCartToStorage(updatedItems, state.userId);

  return {
    ...state,
    promoCode: isApplied ? promocodeInfo.code : null,
    discountValue: isApplied ? promocodeInfo.discountValue : 0,
    items: updatedItems,
    totalQuantity,
    totalPrice,
  };

};

// Remove promo code
const removePromoCode = state => {
  const updatedItems = state.items.map(item => {
    const totalAmount =
      item.pricePerUnit * item.quantity +
      (item.extraPrice || 0) -
      (item.discount || 0); 
    return {...item, promoCode: null, promocodeInfo: null, promoDiscount: 0, totalAmount};
  });

  const {totalQuantity, totalPrice} = calculateTotals(updatedItems);

  saveCartToStorage(updatedItems, state.userId);

  return {
    ...state,
    promoCode: null,
    discountValue: 0,
    items: updatedItems,
    totalQuantity,
    totalPrice,
  };
};

// Helper to persist cart per user
const saveCartToLocalStorage = (state, items, totalQuantity, totalPrice) => {
  if (!state.userId) {return;} // only save if userId exists
  localStorage.setItem(`cart_${state.userId}`, JSON.stringify({
    items,
    totalQuantity,
    totalPrice,
    promoCode: state.promoCode,
    discountValue: state.discountValue,
  }));

};
const onLoginSuccess = (state, payload) => {
  const userId = payload.id;

  const items = getCartItemsByUserId(userId);

  const totalQuantity = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce((s, i) => s + i.totalAmount, 0);

  return {
    ...state,
    userId,
    items,
    totalQuantity,
    totalPrice,
  };
};

const onLogoutSuccess = () => initialState;

export const setUser = (state, userId) => {
  state.userId = userId;

  // Load this user's cart from localStorage if exists
  const savedCart = localStorage.getItem(`CART_ITEMS_${userId}`);
  if (savedCart) {
    const parsedItems = JSON.parse(savedCart) || [];
    const totalQuantity = parsedItems.reduce((s, i) => s + i.quantity, 0);
    const totalPrice = parsedItems.reduce((s, i) => s + i.totalAmount, 0);

    return {
      ...state,
      userId,
      items: parsedItems,
      totalQuantity,
      totalPrice,
    };
  }

  // Otherwise reset cart
  return {...state, items: [], totalQuantity: 0, totalPrice: 0, promoCode: null, discountValue: 0, userId};
};

export default createReducer(initialState, {
  [ADD_TO_CART]: addToCart,
  [SET_USER]: setUser,
  [REMOVE_FROM_CART]: removeFromCart,
  [UPDATE_QUANTITY]: updateQuantity,
  [SET_CART_ITEMS_FROM_STORAGE]: setCartFromStorage,
  [APPLY_PROMO_CODE]: applyPromoCode,
  [LOGIN_SUCCESS]: onLoginSuccess,
  [LOGOUT_SUCCESS]: onLogoutSuccess,
  [IDENTIFY_USER_EXIST_ON_DEVICE_SUCCESSFUL]: onLoginSuccess
  ,
  [REMOVE_PROMO_CODE]: 
  removePromoCode,
  [CLEAR_CART]: (state, payload) => {
    saveCartToStorage([], state.userId); 
    return {
      ...state,
      items: [],
      totalQuantity: 0,
      totalPrice: 0,
      promoCode: null,
      discountValue: 0,
    };
  },
});
